import React, { useCallback, useContext, useMemo } from 'react'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import ExperiencePageTrendingDestinations from 'components/Experiences/ExperiencesPage/ExperiencesPageTrendingDestinations'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import { SearchMenuStates } from 'components/Search/type'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { EXPERIENCE_SEARCH_TYPES, EXPERIENCE_TYPEAHEAD_SEARCH_TYPES } from 'constants/experience'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'
import ExperienceDateFloatingPanel from '../Panels/ExperienceDateFloatingPanel'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'

interface Props {
  closeSearch: () => void
  onSearch: () => void
  isInPage?: boolean
}

function ExperiencesTabContent(props: Props) {
  const { closeSearch, isInPage, onSearch } = props

  const {
    searchItem,
    activeMenu,
    popularDestinations,
    disableRecentSearches,
    checkinDate,
    checkoutDate,
  } = useContext(GlobalSearchStateContext)

  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const recentSearches = useRecentSearches()

  const { locationLabel, locationPlaceholder, whenLabel, anytimePlaceholder, searchLabel, clearAllLabel } = useExperienceSearchLabels()
  const toggleLocationModalVisibility = useCallback(() => {
    const nextMenu = activeMenu === SearchMenuStates.Locations ? SearchMenuStates.Closed : SearchMenuStates.Locations
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: nextMenu })
  }, [activeMenu, searchDispatch])

  const toggleDateModalVisibility = useCallback(() => {
    const nextMenu = activeMenu === SearchMenuStates.Dates ? SearchMenuStates.Closed : SearchMenuStates.Dates
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: nextMenu })
  }, [activeMenu, searchDispatch])

  const recentSearchItems = useMemo(() => {
    return recentSearches.map(recentSearch => ({
      searchItem: recentSearch.searchItem,
      dates: recentSearch.dates,
      recentSearchId: recentSearch.recentSearchId,
      dateAdded: recentSearch.dateAdded,
      rooms: [],
    }))
  }, [recentSearches])

  const dateFieldDisplayValue = useExperienceDateDisplayValue(checkinDate, checkoutDate)

  const handleClearAll = useCallback(() => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [searchDispatch])

  const handleConfirm = useCallback(() => {
    toggleDateModalVisibility()
    onSearch()
  }, [toggleDateModalVisibility, onSearch])

  return (
    <VerticalSpacer gap={24}>
      <div>
        <LocationFloatingPanel
          isOpen={activeMenu === SearchMenuStates.Locations}
          onClose={toggleLocationModalVisibility}
          onGoBack={toggleLocationModalVisibility}
          onConfirm={toggleLocationModalVisibility}
          searchTypes={EXPERIENCE_SEARCH_TYPES}
          typeaheadVertical="experience"
          typeaheadTypes={EXPERIENCE_TYPEAHEAD_SEARCH_TYPES}
          popularPlaces={popularDestinations}
          recentSearches={disableRecentSearches ? undefined : recentSearchItems}
        />
        <ExperienceDateFloatingPanel
          isOpen={activeMenu === SearchMenuStates.Dates}
          onClose={toggleDateModalVisibility}
          onGoBack={toggleDateModalVisibility}
          confirmButtonDisabled={false}
          confirmText={searchLabel}
          secondaryActionText={clearAllLabel}
          onSecondaryActionClick={handleClearAll}
          onConfirm={handleConfirm}
        />
        <VerticalSpacer gap={12}>
          <SearchFormField
            label={locationLabel}
            placeholder={locationPlaceholder}
            value={searchItem?.format.mainText}
            onClick={toggleLocationModalVisibility}
          />
          <SearchFormField
            label={whenLabel}
            placeholder={anytimePlaceholder}
            value={dateFieldDisplayValue}
            onClick={toggleDateModalVisibility}
          />
        </VerticalSpacer>
      </div>
      {!isInPage && <ExperiencePageTrendingDestinations
        colour="neutral-two"
        weight="normal"
        onClick={closeSearch}
      />}
    </VerticalSpacer>
  )
}

export default ExperiencesTabContent
